import { Result, Schema } from "effect";
import { Instant, NonNegativeInt, Rating, Reviewed } from "../srs/types.ts";
import { Answer, CardId, ReviewId, Rev } from "./model/types.ts";
import { SessionCard } from "./view/session-card.ts";
import { SetSummary } from "./view/sets.ts";

export const ReviewResult = Schema.Struct({
  reviewId: ReviewId,
  cardId: CardId,
  reviewedAt: Instant,
  rating: Rating,
  card: Reviewed,
}).check(
  Schema.makeFilter(
    (r) => r.card.memory.lastReviewAt === r.reviewedAt || "the card's last review must be this review",
  ),
);
export type ReviewResult = typeof ReviewResult.Type;

export const SetStatus = Schema.Struct({ due: NonNegativeInt, nextDueAt: Schema.NullOr(Instant) });
export type SetStatus = typeof SetStatus.Type;

export const QueueReply = Schema.Struct({ cards: Schema.Array(SessionCard), set: SetStatus });
export type QueueReply = typeof QueueReply.Type;

export const SetList = Schema.Array(SetSummary);
export type SetList = typeof SetList.Type;

// Immutable once built: the client resends it byte for byte on Retry. reps is the card's version.
export const ReviewRequest = Schema.Struct({
  reviewId: ReviewId,
  reps: NonNegativeInt,
  contentRev: Rev,
  settingsRev: Rev,
  given: Answer,
});
export type ReviewRequest = typeof ReviewRequest.Type;

const decodeRequest = Schema.decodeUnknownResult(ReviewRequest);

// Trims and NFC-normalises the answer and lower-cases the id before checking, so a retry that differs only in
// those ways binds to the same attempt.
function normalise(body: unknown): unknown {
  if (typeof body !== "object" || body === null) return body;
  const { given, reviewId } = body as Record<string, unknown>;
  return {
    ...body,
    ...(typeof given === "string" ? { given: given.trim().normalize("NFC") } : {}),
    ...(typeof reviewId === "string" ? { reviewId: reviewId.toLowerCase() } : {}),
  };
}

export function decodeReviewRequest(body: unknown): Result.Result<ReviewRequest, string> {
  return decodeRequest(normalise(body)).pipe(Result.mapError((e) => e.message));
}

// result is historical and immutable; set is current at reply time.
export const ReviewReply = Schema.Struct({ result: ReviewResult, duplicate: Schema.Boolean, set: SetStatus });
export type ReviewReply = typeof ReviewReply.Type;

// PUT /api/settings/hanzi-prompts: pause (true) or unpause the meaning and pinyin kinds in every set.
export const PauseRequest = Schema.Struct({ paused: Schema.Boolean });
export type PauseRequest = typeof PauseRequest.Type;

const decodePause = Schema.decodeUnknownResult(PauseRequest);

export function decodePauseRequest(body: unknown): Result.Result<PauseRequest, string> {
  return decodePause(body).pipe(Result.mapError((e) => e.message));
}

const Text = (max: number) => Schema.String.check(Schema.isTrimmed(), Schema.isNonEmpty(), Schema.isMaxLength(max));

export const AddWordRequest = Schema.Struct({
  english: Text(100),
  word: Schema.optionalKey(Text(20)),
  pos: Schema.optionalKey(Text(20)),
});
export type AddWordRequest = typeof AddWordRequest.Type;

const decodeAddWord = Schema.decodeUnknownResult(AddWordRequest);

export function decodeAddWordRequest(body: unknown): Result.Result<AddWordRequest, string> {
  const trimmed =
    typeof body === "object" && body !== null
      ? Object.fromEntries(Object.entries(body).map(([k, v]) => [k, typeof v === "string" ? v.trim().normalize("NFC") : v]))
      : body;
  return decodeAddWord(trimmed).pipe(Result.mapError((e) => e.message));
}

export const DictionaryWord = Schema.Struct({
  word: Schema.String,
  pinyin: Schema.String,
  meaning: Schema.String,
  meanings: Schema.Array(Schema.String),
  frequency: NonNegativeInt,
});
export type DictionaryWord = typeof DictionaryWord.Type;

export const LookupReply = Schema.Struct({ english: Schema.String, words: Schema.Array(DictionaryWord) });
export type LookupReply = typeof LookupReply.Type;

export const AddWordReply = Schema.Struct({
  added: Schema.Boolean,
  word: DictionaryWord,
  cards: Schema.Array(CardId),
  alternatives: Schema.Array(DictionaryWord),
});
export type AddWordReply = typeof AddWordReply.Type;

export const CONFLICT_CODES = ["stale_card", "stale_content", "review_id_reused", "not_available"] as const;
export type ConflictCode = (typeof CONFLICT_CODES)[number];

export const ERROR_CODES = ["bad_request", "not_found", ...CONFLICT_CODES, "internal", "busy"] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const ErrorReply = Schema.Struct({ error: Schema.String, code: Schema.Literals(ERROR_CODES), requestId: Schema.String });
export type ErrorReply = typeof ErrorReply.Type;

export const isConflictCode = (code: ErrorCode): code is ConflictCode => (CONFLICT_CODES as readonly string[]).includes(code);

// Reply decoders for the client: a failure is the schema's message.
const decoder = <T>(decode: (u: unknown) => Result.Result<T, { readonly message: string }>) => (body: unknown) =>
  decode(body).pipe(Result.mapError((e) => e.message));

export const decodeSetList = decoder(Schema.decodeUnknownResult(SetList));
export const decodeQueueReply = decoder(Schema.decodeUnknownResult(QueueReply));
export const decodeReviewReply = decoder(Schema.decodeUnknownResult(ReviewReply));
export const decodeErrorReply = decoder(Schema.decodeUnknownResult(ErrorReply));
